import React, { useEffect, useRef, useState } from 'react'
import {
  Route,
  Switch,
  Redirect,
  useParams,
  useHistory,
  useLocation,
} from 'react-router-dom'
import {
  ABSpinner,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import API from '../../utils/API'
import QuestOverview from './QuestOverview'
import ExperienceBuild from './ExperienceBuild'
import ExperienceWorkspace from './ExperienceWorkspace'
import ExperienceBadge from './ExperienceBadge'
import ExperienceManageRail from './ExperienceManageRail'
import QuestsParticipants from './QuestParticipants'
import QuestsInquiries from './QuestsInquiries'
import QuestPayments from './QuestPayments'
import CopyQuestModal from './CopyQuestModal'
import { ExperiencePageHead } from '../experienceUI'
import {
  resolveExperienceLocation,
  buildExperiencePath,
  getBuildItemByKey,
  MANAGE_ITEMS,
} from './experienceNavigation'
import {
  getStatusBadgeMeta,
  getModebarPills,
  getExperienceScenario,
  getOverviewPublishBlockers,
} from './experienceStatus'
import { UTCformatDateInPlan } from '../../utils/date'
import { useExperienceUnsavedGuard } from './questAutosave'

const formatManageCount = (n, cappedAt) => {
  if (n == null || Number.isNaN(n)) return null
  if (cappedAt != null && n >= cappedAt) return `${cappedAt}+`
  return String(n)
}

/** Compact money for manage rail (v7 `$61k` style). */
const formatManageCollectedAmount = (value) => {
  if (value == null || value === '') return null
  const n = Number(value)
  if (Number.isNaN(n)) return null
  const abs = Math.abs(n)
  if (abs >= 1000) {
    const k = n / 1000
    const rounded = Math.abs(k) >= 100 ? Math.round(k) : Math.round(k * 10) / 10
    const label = Number.isInteger(rounded)
      ? String(rounded)
      : rounded.toFixed(1).replace(/\.0$/, '')
    return `$${label}k`
  }
  return `$${Math.round(n).toLocaleString()}`
}

const CreateQuest = () => {
  const { questId } = useParams()
  const history = useHistory()
  const location = useLocation()
  const Error = useErrorService()
  const Notification = useNotificationService()
  const [questDetail, setQuestDetail] = useState(null)
  const questDetailRef = useRef(null)
  questDetailRef.current = questDetail
  // Match legacy QuestDetail: start loading when opening an existing experience
  // so Enrollment → Signup does not mount before networkId is available.
  const [isLoading, setIsLoading] = useState(!!questId)
  const [isPublished, setIsPublished] = useState(false)
  const [isStatusUpdating, setIsStatusUpdating] = useState(false)
  const [openStageKey, setOpenStageKey] = useState('setup')
  const [saveState, setSaveState] = useState('saved') // saved | saving | error
  useExperienceUnsavedGuard()
  const [openHeaderMenu, setOpenHeaderMenu] = useState(null) // 'preview' | 'publish' | null
  const headerActsRef = useRef(null)
  const prefillUsedRef = useRef(new Set())
  const [isNewlyCopied, setIsNewlyCopied] = useState(false)
  const [copiedFromTitle, setCopiedFromTitle] = useState('')
  const [manageActions, setManageActions] = useState(null)
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [isCopying, setIsCopying] = useState(false)
  const [selectedQuestForCopy, setSelectedQuestForCopy] = useState(null)
  const [manageRefreshKey, setManageRefreshKey] = useState(0)
  const [manageCounts, setManageCounts] = useState({
    'manage-participants': '0',
    'manage-payments': '$0',
    'manage-inquiries': '0',
  })
  // Keep each Manage panel mounted after first visit so tab switches
  // do not remount → refetch → feather loader.
  const [mountedManagePanels, setMountedManagePanels] = useState({
    'manage-participants': false,
    'manage-payments': false,
    'manage-inquiries': false,
  })
  const [overview, setOverview] = useState(null)
  const [overviewLoading, setOverviewLoading] = useState(false)
  const [overviewError, setOverviewError] = useState(null)
  const [clientReadiness, setClientReadiness] = useState(null)
  const overviewRequestIdRef = useRef(0)
  const isNewQuest = !questId

  const loc = resolveExperienceLocation(location.pathname, questId)
  const scenario = getExperienceScenario(questDetail, {
    isNewQuest,
    isNewlyCopied,
    overview,
  })
  const statusMeta = getStatusBadgeMeta(questDetail, {
    isNewQuest,
    isNewlyCopied,
    overview,
  })
  const modebarPills = getModebarPills(
    questDetail,
    scenario,
    loc.mode === 'build'
      ? clientReadiness || overview?.readiness
      : overview?.readiness || clientReadiness,
  )
  const manageEnabled = statusMeta.manageEnabled

  useEffect(() => {
    let cancelled = false
    const getQuestDetail = async () => {
      try {
        if (!isNewQuest && questId) {
          // A quest created from create-new is already on screen (the first
          // save swaps the URL to its id); refetch quietly instead of
          // replacing the whole Build with a spinner.
          const alreadyShown = questDetailRef.current?._id === questId
          if (!cancelled && !alreadyShown) setIsLoading(true)
          const prefillData =
            location && location.state && location.state.prefill
          const newlyCopied =
            location && location.state && location.state.isNewlyCopied
          const fromTitle =
            (location && location.state && location.state.copiedFromTitle) || ''

          const shouldUsePrefill =
            prefillData &&
            newlyCopied &&
            prefillData._id === questId &&
            !prefillUsedRef.current.has(questId)

          if (newlyCopied) {
            setIsNewlyCopied(true)
            if (fromTitle) {
              setCopiedFromTitle(fromTitle)
            }
            try {
              sessionStorage.setItem(
                `experience-copied-${questId}`,
                JSON.stringify({ copiedFromTitle: fromTitle || '' }),
              )
            } catch (storageError) {
              // ignore
            }
          } else if (questId) {
            try {
              const raw = sessionStorage.getItem(`experience-copied-${questId}`)
              if (raw) {
                const parsed = JSON.parse(raw)
                setIsNewlyCopied(true)
                if (parsed?.copiedFromTitle) {
                  setCopiedFromTitle(parsed.copiedFromTitle)
                }
              }
            } catch (storageError) {
              // ignore
            }
          }

          if (shouldUsePrefill) {
            prefillUsedRef.current.add(questId)
            if (!cancelled) {
              setQuestDetail(prefillData)
              setIsPublished(
                prefillData?.isPublished || prefillData?.published || false,
              )
            }
          }

          const { data } = await API.AdminAPI.quests.getQuestDetail(questId)
          if (cancelled) return
          setQuestDetail(data)
          setIsPublished(data?.isPublished || data?.published || false)
          setIsLoading(false)
        } else if (!cancelled) {
          setIsLoading(false)
        }
      } catch (error) {
        if (!cancelled) {
          Error.showError(error)
          setIsLoading(false)
        }
      }
    }

    getQuestDetail()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId])

  // Clear overview when switching experiences so a late response cannot bleed.
  useEffect(() => {
    setOverview(null)
    setOverviewError(null)
    setOverviewLoading(false)
    setClientReadiness(null)
  }, [questId])

  const loadOverview = async (requestQuestId = questId) => {
    if (!requestQuestId) {
      return
    }
    const requestId = overviewRequestIdRef.current + 1
    overviewRequestIdRef.current = requestId
    setOverviewLoading(true)
    setOverviewError(null)
    try {
      const { data } = await API.AdminAPI.quests.getQuestOverview(
        requestQuestId,
      )
      if (overviewRequestIdRef.current !== requestId) {
        return
      }
      setOverview(data)
      setOverviewLoading(false)
    } catch (error) {
      if (overviewRequestIdRef.current !== requestId) {
        return
      }
      setOverview(null)
      setOverviewError(error)
      setOverviewLoading(false)
    }
  }

  // Fetch overview only on the Overview tab (and not for create-new).
  useEffect(() => {
    if (isNewQuest || !questId || loc.mode !== 'overview') {
      return undefined
    }
    let cancelled = false
    const requestId = overviewRequestIdRef.current + 1
    overviewRequestIdRef.current = requestId
    setOverviewLoading(true)
    setOverviewError(null)

    const fetchOverview = async () => {
      try {
        const { data } = await API.AdminAPI.quests.getQuestOverview(questId)
        if (cancelled || overviewRequestIdRef.current !== requestId) {
          return
        }
        setOverview(data)
        setOverviewLoading(false)
      } catch (error) {
        if (cancelled || overviewRequestIdRef.current !== requestId) {
          return
        }
        setOverview(null)
        setOverviewError(error)
        setOverviewLoading(false)
      }
    }

    fetchOverview()
    return () => {
      cancelled = true
    }
  }, [questId, isNewQuest, loc.mode])

  useEffect(() => {
    if (questDetail) {
      setIsPublished(questDetail.isPublished || questDetail.published || false)
    }
  }, [questDetail])

  useEffect(() => {
    if (loc.mode === 'build' && loc.buildKey) {
      const item = getBuildItemByKey(loc.buildKey)
      if (item?.stageKey) {
        setOpenStageKey(item.stageKey)
      }
    }
  }, [loc.mode, loc.buildKey])

  // Rail badges: fetch once when entering Manage (not on every sub-tab switch).
  // Re-run only when Manage is re-entered (manageRefreshKey) or quest changes.
  useEffect(() => {
    if (!questId || !manageEnabled || loc.mode !== 'manage') {
      return undefined
    }
    let cancelled = false

    setManageCounts({
      'manage-participants': '0',
      'manage-payments': '$0',
      'manage-inquiries': '0',
    })

    const loadManageCounts = async () => {
      // Payment totals come from participants summary; admin/payment is list-only.
      const [participantsResult, inquiriesResult] = await Promise.all([
        API.AdminAPI.quests.getParticipants(questId).catch(() => null),
        API.AdminAPI.quests.getInquirie(questId).catch(() => null),
      ])

      if (cancelled) return

      const participantsPayload = participantsResult?.data
      const participants = Array.isArray(participantsPayload?.participants)
        ? participantsPayload.participants
        : []
      const participantsSummary = participantsPayload?.summary
      // Match Participants "On the roster" (enrolled), not total list length.
      const onRosterCount =
        participantsSummary?.enrolledCount ??
        participantsSummary?.totalParticipants ??
        participants.length
      const collectedAmount = participantsSummary?.collectedAmount
      const inquiries = Array.isArray(inquiriesResult?.data)
        ? inquiriesResult.data
        : []

      setManageCounts({
        'manage-participants': formatManageCount(onRosterCount) || '0',
        'manage-payments': formatManageCollectedAmount(collectedAmount) || '$0',
        'manage-inquiries': formatManageCount(inquiries.length) || '0',
      })
    }

    loadManageCounts()
    return () => {
      cancelled = true
    }
  }, [questId, manageEnabled, loc.mode, manageRefreshKey])

  // Lazy-mount manage panels; reset when leaving Manage so next enter is fresh.
  useEffect(() => {
    if (loc.mode !== 'manage') {
      setMountedManagePanels({
        'manage-participants': false,
        'manage-payments': false,
        'manage-inquiries': false,
      })
      return
    }
    const key = loc.manageKey || 'manage-participants'
    setMountedManagePanels((prev) =>
      prev[key] ? prev : { ...prev, [key]: true },
    )
  }, [loc.mode, loc.manageKey])

  useEffect(() => {
    if (!openHeaderMenu) return undefined
    const onDocClick = (event) => {
      if (
        headerActsRef.current &&
        !headerActsRef.current.contains(event.target)
      ) {
        setOpenHeaderMenu(null)
      }
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenHeaderMenu(null)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [openHeaderMenu])

  const goOverview = () => {
    if (isNewQuest) {
      history.push('/admin/experiences/create-new')
      return
    }
    history.push(buildExperiencePath(questId, 'overview'))
  }

  const goBuild = (stage, target) => {
    let key = target
    if (!key && stage) {
      const stageDefaults = {
        setup: 'setup-name',
        sell: 'content-hero',
        enrol: 'enrol-signup',
        travel: 'prep-learning',
      }
      key = stageDefaults[stage] || 'setup-name'
    }
    const resolvedKey = key || 'setup-name'
    const item = getBuildItemByKey(resolvedKey)
    // Set stage before the route change so Build does not mount with a stale
    // openStageKey (e.g. setup) and replace the deep link with Experience Details.
    if (item?.stageKey) {
      setOpenStageKey(item.stageKey)
    } else if (stage) {
      setOpenStageKey(stage)
    }
    history.push(buildExperiencePath(questId || null, 'build', resolvedKey))
  }

  const goManage = (manageKey) => {
    if (!manageEnabled || isNewQuest) {
      return
    }
    // Only refresh rail counts when entering Manage from Overview/Build —
    // not when switching Participants ↔ Payments ↔ Inquiries.
    if (loc.mode !== 'manage') {
      setManageRefreshKey((key) => key + 1)
    }
    history.push(
      buildExperiencePath(
        questId,
        'manage',
        manageKey || 'manage-participants',
      ),
    )
  }

  const refreshManageCounts = () => {
    setManageRefreshKey((key) => key + 1)
  }

  const onSelectBuildItem = (key) => {
    const item = getBuildItemByKey(key)
    if (item?.stageKey) {
      setOpenStageKey(item.stageKey)
    }
    // The ref is fresh even when Next awaited the first save (which creates the
    // quest) with this callback captured before the URL had its id.
    history.push(
      buildExperiencePath(
        questId || questDetailRef.current?._id || null,
        'build',
        key,
      ),
    )
  }

  const onStageToggle = (stageKey, opts = {}) => {
    if (opts.forceOpen || openStageKey !== stageKey) {
      setOpenStageKey(stageKey)
      return
    }
    setOpenStageKey(stageKey)
  }

  const overviewPublishBlockers = getOverviewPublishBlockers(overview)
  const isPublishBlockedByOverview =
    !isPublished &&
    loc.mode === 'overview' &&
    !!overview &&
    overviewPublishBlockers.length > 0
  const publishBlocked =
    statusMeta.publishDisabled || isPublishBlockedByOverview

  const questPublish = async () => {
    if (isStatusUpdating || !questId || questDetail?.isArchived) return
    if (!isPublished && publishBlocked) return
    try {
      const nextPublishedState = !isPublished
      setIsStatusUpdating(true)
      const { data } = await API.AdminAPI.quests.publishQuest(questId, {
        isPublished: nextPublishedState,
      })
      setQuestDetail((prev) => ({
        ...(prev || {}),
        ...(data || {}),
        isPublished: nextPublishedState,
        published: nextPublishedState,
      }))
      Notification.showNotification(
        nextPublishedState
          ? 'Experience published successfully!'
          : 'Experience unpublished successfully!',
        'success',
      )
      setIsPublished(nextPublishedState)
      if (nextPublishedState) {
        setIsNewlyCopied(false)
      }
      // Refresh overview so status / readiness match the new publish state.
      if (loc.mode === 'overview') {
        loadOverview(questId)
      } else {
        history.push(buildExperiencePath(questId, 'overview'))
      }
    } catch (e) {
      if (e?.code === 'quest_not_ready_to_publish') {
        Notification.showNotification(
          e.message || 'This experience is not ready to publish yet.',
          'danger',
        )
        if (loc.mode !== 'overview') {
          history.push(buildExperiencePath(questId, 'overview'))
        }
        loadOverview(questId)
        return
      }
      Error.showError(e)
    } finally {
      setIsStatusUpdating(false)
    }
  }

  const title =
    questDetail?.name ||
    questDetail?.title ||
    (isNewQuest ? 'Untitled experience' : '—')

  const manageNotes = {
    'manage-participants': manageCounts['manage-participants'],
    'manage-payments': manageCounts['manage-payments'],
    'manage-inquiries': manageCounts['manage-inquiries'],
  }

  if (isLoading) {
    return <ABSpinner />
  }

  const prosperityUrl = questId
    ? `${process.env.REACT_APP_CLIENT_DOMAIN}/experience/${questId}`
    : ''

  const toggleHeaderMenu = (menu) => {
    setOpenHeaderMenu((prev) => (prev === menu ? null : menu))
  }

  const copyProsperityLink = async () => {
    if (!prosperityUrl) {
      Notification.showNotification('Experience ID not found', 'danger')
      return
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(prosperityUrl)
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = prosperityUrl
        textArea.setAttribute('readonly', '')
        textArea.style.position = 'absolute'
        textArea.style.left = '-9999px'
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
      Notification.showNotification(
        'Public URL copied to clipboard!',
        'success',
      )
      setOpenHeaderMenu(null)
    } catch (err) {
      Notification.showNotification('Failed to copy URL', 'danger')
    }
  }

  const openProsperityPage = () => {
    if (!prosperityUrl) return
    window.open(prosperityUrl, '_blank', 'noopener,noreferrer')
    setOpenHeaderMenu(null)
  }

  // Completed / reuse: copy THIS experience (skip the "pick a source" modal).
  const openCopyThisExperience = () => {
    if (!questId || !questDetail) return
    const sourceName = questDetail.name || questDetail.title || 'Experience'
    setSelectedQuestForCopy({
      value: questId,
      label: sourceName,
      extra: questDetail,
    })
    setShowCopyModal(true)
  }

  const handleCloseCopyModal = () => {
    if (isCopying) return
    setShowCopyModal(false)
    setSelectedQuestForCopy(null)
  }

  const handleConfirmCopyExperience = async (
    selectedId,
    selectedQuest,
    questName,
    formValues,
  ) => {
    if (!selectedId || !questName) return
    try {
      setIsCopying(true)
      const body = {
        existingQuestId: selectedId,
        questName: questName.trim(),
        ...(formValues?.questStartDate && {
          questStartDate: UTCformatDateInPlan(formValues.questStartDate),
        }),
        ...(formValues?.questEndDate && {
          questEndDate: UTCformatDateInPlan(formValues.questEndDate),
        }),
        itinerary: formValues.itinerary || [],
      }
      const { data } = await API.AdminAPI.quests.copyQuest(body)
      if (data && data._id) {
        setShowCopyModal(false)
        setSelectedQuestForCopy(null)
        const fromTitle =
          selectedQuest?.name ||
          selectedQuest?.title ||
          questDetail?.name ||
          questDetail?.title ||
          ''
        try {
          sessionStorage.setItem(
            `experience-copied-${data._id}`,
            JSON.stringify({ copiedFromTitle: fromTitle }),
          )
        } catch (storageError) {
          // ignore storage failures
        }
        history.push({
          pathname: `/admin/experiences/${data._id}/overview`,
          state: {
            prefill: data,
            isNewlyCopied: true,
            copiedFromTitle: fromTitle,
          },
        })
        Notification.showNotification(
          'Experience copied successfully!',
          'success',
        )
      }
    } catch (e) {
      Error.showError(e)
    } finally {
      setIsCopying(false)
    }
  }

  const renderManageBody = () => {
    if (!manageEnabled) {
      const isCopiedEmpty = scenario === 'copied' || isNewlyCopied
      return (
        <div className='experience-editor-main'>
          <div className='experience-manage-empty'>
            <div className='experience-empty-icon'>—</div>
            <h3>Nothing to manage yet</h3>
            <p>
              {isCopiedEmpty
                ? 'The roster, payments and inquiries belong to the original experience and start empty here. Publish this draft before enrollments and payments appear.'
                : 'This experience is still a draft. Publish the Prosperity page before enquiries arrive, and open enrollment before participants or payments appear.'}
            </p>
            <button
              type='button'
              className='btn-navy mt-15px'
              onClick={() => goBuild('setup')}>
              Continue building
            </button>
          </div>
        </div>
      )
    }

    const manageKey = loc.manageKey || 'manage-participants'
    const manageItem =
      MANAGE_ITEMS.find((m) => m.key === manageKey) || MANAGE_ITEMS[0]
    return (
      <div className='experience-editor-main experience-editor-main--fill'>
        <ExperienceWorkspace
          kicker='Manage'
          title={manageItem.label}
          description={manageItem.description}
          actions={manageKey === 'manage-participants' ? manageActions : null}>
          {mountedManagePanels['manage-participants'] ? (
            <div
              className={
                manageKey === 'manage-participants'
                  ? 'experience-manage-panel'
                  : 'experience-manage-panel is-hidden'
              }
              aria-hidden={manageKey !== 'manage-participants'}>
              <QuestsParticipants
                questDetail={questDetail}
                onHeaderActionsChange={setManageActions}
                onManageDataChange={refreshManageCounts}
              />
            </div>
          ) : null}
          {mountedManagePanels['manage-payments'] ? (
            <div
              className={
                manageKey === 'manage-payments'
                  ? 'experience-manage-panel'
                  : 'experience-manage-panel is-hidden'
              }
              aria-hidden={manageKey !== 'manage-payments'}>
              <QuestPayments questId={questId} />
            </div>
          ) : null}
          {mountedManagePanels['manage-inquiries'] ? (
            <div
              className={
                manageKey === 'manage-inquiries'
                  ? 'experience-manage-panel'
                  : 'experience-manage-panel is-hidden'
              }
              aria-hidden={manageKey !== 'manage-inquiries'}>
              <QuestsInquiries
                questDetail={questDetail}
                onManageDataChange={refreshManageCounts}
              />
            </div>
          ) : null}
        </ExperienceWorkspace>
      </div>
    )
  }

  return (
    <main className='experience'>
      <div className='experience-editor'>
        <ExperiencePageHead
          actsRef={headerActsRef}
          crumb={
            <>
              <button
                type='button'
                onClick={() => history.push('/admin/experiences')}>
                Experiences
              </button>{' '}
              ›
            </>
          }
          title={<span>{title}</span>}
          titleAfter={statusMeta.chips.map((chip) => (
            <ExperienceBadge
              key={chip.label}
              title={chip.label}
              variant={
                chip.className === 'badge-live'
                  ? 'green'
                  : chip.className === 'badge-soon'
                  ? 'amber'
                  : chip.className === 'badge-run'
                  ? 'steel'
                  : chip.className === 'badge-done'
                  ? 'gray'
                  : chip.className === 'badge-draft'
                  ? 'draft'
                  : 'taupe'
              }
            />
          ))}>
          <span
            className={`experience-saved${
              saveState === 'error' ? ' is-error' : ''
            }${saveState === 'saving' ? ' is-saving' : ''}`}>
            <i />
            {saveState === 'saving'
              ? 'Saving…'
              : saveState === 'error'
              ? 'Couldn’t save'
              : 'All changes saved'}
          </span>

          {!isNewQuest ? (
            <div className='experience-header-menu-wrap'>
              <button
                type='button'
                className='btn-outline-experience'
                disabled={!questId}
                aria-haspopup='true'
                aria-expanded={openHeaderMenu === 'preview'}
                onClick={() => toggleHeaderMenu('preview')}>
                Preview ▾
              </button>
              {openHeaderMenu === 'preview' ? (
                <div className='experience-header-menu' role='menu'>
                  <h4>Preview as</h4>
                  <div className='experience-menu-row'>
                    <span className='experience-menu-ico' aria-hidden='true'>
                      🌐
                    </span>
                    <div className='experience-menu-copy'>
                      <b>Prosperity page</b>
                      <small>What anyone with the link sees</small>
                      <div className='experience-preview-acts'>
                        <button
                          type='button'
                          className='btn-outline-experience btn-sm-experience'
                          onClick={openProsperityPage}>
                          Open Page
                        </button>
                        <button
                          type='button'
                          className='btn-outline-experience btn-sm-experience'
                          onClick={copyProsperityLink}>
                          Copy link
                        </button>
                      </div>
                    </div>
                  </div>
                  <button
                    type='button'
                    className='experience-menu-item'
                    onClick={() => {
                      setOpenHeaderMenu(null)
                      goBuild('enrol', 'enrol-signup')
                    }}>
                    <span aria-hidden='true'>✉️</span>
                    <span>
                      Signup page
                      <small>What an invited guest sees first</small>
                    </span>
                  </button>
                  <div className='experience-menu-sep' />
                  <button
                    type='button'
                    className='experience-menu-item'
                    onClick={() => {
                      setOpenHeaderMenu(null)
                      goBuild('enrol', 'enrol-email')
                    }}>
                    <span aria-hidden='true'>📧</span>
                    <span>
                      Welcome email
                      <small>Send a test to yourself</small>
                    </span>
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          {!isNewQuest && !questDetail?.isArchived ? (
            <div className='experience-header-menu-wrap'>
              <button
                type='button'
                className='btn-navy'
                disabled={
                  !questId ||
                  isStatusUpdating ||
                  statusMeta.publishDisabled ||
                  (!isPublished && publishBlocked)
                }
                title={
                  !isPublished && overviewPublishBlockers.length
                    ? `Finish before publishing: ${overviewPublishBlockers
                        .slice(0, 3)
                        .map((b) => b.title)
                        .join('; ')}${
                        overviewPublishBlockers.length > 3 ? '…' : ''
                      }`
                    : ''
                }
                aria-haspopup={
                  isPublished && scenario !== 'completed' ? 'true' : undefined
                }
                aria-expanded={
                  isPublished && scenario !== 'completed'
                    ? openHeaderMenu === 'publish'
                    : undefined
                }
                onClick={() => {
                  if (scenario === 'completed' || statusMeta.publishDisabled) {
                    return
                  }
                  if (isPublished) {
                    toggleHeaderMenu('publish')
                  } else {
                    questPublish()
                  }
                }}>
                {scenario === 'completed'
                  ? 'Completed'
                  : isPublished
                  ? 'Published ▾'
                  : `${statusMeta.publishLabel || 'Publish'}`}
              </button>
              {isPublished &&
              scenario !== 'completed' &&
              openHeaderMenu === 'publish' ? (
                <div
                  className='experience-header-menu experience-header-menu-sm'
                  role='menu'>
                  <button
                    type='button'
                    className='experience-menu-item'
                    disabled={isStatusUpdating}
                    onClick={() => {
                      setOpenHeaderMenu(null)
                      questPublish()
                    }}>
                    <span>
                      Unpublish
                      <small>Hide the Prosperity page from the public</small>
                    </span>
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </ExperiencePageHead>

        <div className='experience-modebar'>
          <div className='experience-mode-tabs'>
            <button
              type='button'
              className={`experience-mode-tab${
                loc.mode === 'overview' ? ' sel' : ''
              }`}
              onClick={goOverview}>
              Overview
            </button>
            <button
              type='button'
              className={`experience-mode-tab${
                loc.mode === 'build' ? ' sel' : ''
              }`}
              onClick={() => goBuild(openStageKey || 'setup')}>
              Build
            </button>
            <button
              type='button'
              className={`experience-mode-tab${
                loc.mode === 'manage' ? ' sel' : ''
              }${!manageEnabled ? ' is-disabled' : ''}`}
              disabled={!manageEnabled}
              title={
                manageEnabled
                  ? ''
                  : 'Manage becomes available once this experience can receive activity.'
              }
              onClick={() => goManage()}>
              Manage
            </button>
          </div>
          <span className='experience-mode-spacer' />
          {modebarPills.map((pill) => (
            <span
              key={pill.key}
              className={`experience-ready-pill ${pill.tone || 'warn'}`}>
              {pill.text}
            </span>
          ))}
        </div>

        <div className='experience-body'>
          <Switch location={location}>
            {/* Blank create — Overview first ("Untitled experience") */}
            <Route
              exact
              path={[
                '/admin/experiences/create-new',
                '/admin/experiences/create-new/overview',
              ]}>
              <QuestOverview
                questDetail={questDetail}
                overview={null}
                overviewLoading={false}
                overviewError={null}
                isNewQuest
                isNewlyCopied={false}
                copiedFromTitle=''
                onOpenBuild={goBuild}
                onOpenManage={goManage}
              />
            </Route>

            {/* Blank create — Build */}
            <Route
              exact
              path={[
                '/admin/experiences/create-new/build',
                '/admin/experiences/create-new/build/:sectionKey',
              ]}>
              <ExperienceBuild
                questDetail={questDetail || {}}
                setQuestDetail={setQuestDetail}
                isLoading={isLoading}
                activeItemKey={loc.buildKey || 'setup-name'}
                onSelectItem={onSelectBuildItem}
                openStageKey={openStageKey}
                onStageToggle={onStageToggle}
                onSaveStateChange={setSaveState}
                onClientReadinessChange={setClientReadiness}
                isSaving={saveState === 'saving'}
                isNewlyCopied={false}
              />
            </Route>

            {/* Overview (default + explicit) */}
            <Route
              exact
              path={[
                '/admin/experiences/:questId',
                '/admin/experiences/:questId/overview',
              ]}>
              <QuestOverview
                questDetail={questDetail}
                overview={overview}
                prepReadiness={clientReadiness?.prep}
                overviewLoading={overviewLoading}
                overviewError={overviewError}
                onRetryOverview={() => loadOverview(questId)}
                isNewQuest={false}
                isNewlyCopied={isNewlyCopied}
                copiedFromTitle={copiedFromTitle}
                onOpenBuild={goBuild}
                onOpenManage={goManage}
                onCopyExperience={openCopyThisExperience}
              />
            </Route>

            {/* Build */}
            <Route
              exact
              path={[
                '/admin/experiences/:questId/build',
                '/admin/experiences/:questId/build/:sectionKey',
                '/admin/experiences/:questId/details',
                '/admin/experiences/:questId/enrollment-typeform',
                '/admin/experiences/:questId/itinerary',
                '/admin/experiences/:questId/learning',
              ]}>
              <ExperienceBuild
                questDetail={questDetail || {}}
                setQuestDetail={setQuestDetail}
                isLoading={isLoading}
                activeItemKey={loc.buildKey || 'setup-name'}
                onSelectItem={onSelectBuildItem}
                openStageKey={openStageKey}
                onStageToggle={onStageToggle}
                onSaveStateChange={setSaveState}
                onClientReadinessChange={setClientReadiness}
                isSaving={saveState === 'saving'}
                isNewlyCopied={isNewlyCopied}
              />
            </Route>

            {/* Manage */}
            <Route
              exact
              path={[
                '/admin/experiences/:questId/manage',
                '/admin/experiences/:questId/manage/:manageKey',
                '/admin/experiences/:questId/participants',
                '/admin/experiences/:questId/payment',
                '/admin/experiences/:questId/inquiries',
              ]}>
              {!manageEnabled ? (
                <Redirect to={buildExperiencePath(questId, 'overview')} />
              ) : (
                <div className='experience-cols'>
                  <ExperienceManageRail
                    questDetail={questDetail}
                    activeKey={loc.manageKey || 'manage-participants'}
                    onSelect={goManage}
                    notes={manageNotes}
                  />
                  <div className='experience-editor-pane'>
                    {renderManageBody()}
                  </div>
                </div>
              )}
            </Route>

            <Redirect to={buildExperiencePath(questId, 'overview')} />
          </Switch>
        </div>
      </div>
      <CopyQuestModal
        show={showCopyModal}
        onHide={handleCloseCopyModal}
        selectedQuest={selectedQuestForCopy}
        onConfirmCopy={handleConfirmCopyExperience}
        isLoading={isCopying}
      />
    </main>
  )
}

export default CreateQuest
